import { useContext, useMemo } from 'react'

import { SanityImageFragment } from '@data/sanity/queries/types/image'
import { PrintShopFormContext } from '@lib/print/print-shop-form-context'
import { PrintShopFormStringsContext } from '@lib/print/print-shop-form-strings-context'
import {
  getUploadSectionDescription,
  getUploadSectionTitle,
} from '@lib/print/text'
import { ShopFormStepProps } from '@lib/print/types'

import ComplexPortableText from '@components/portable-text/complex'
import ShopFormBackButton from '../back-button'
import ShopFormNextButton from '../next-button'
import UploadStepPrintPreview from './print-preview'
import UploadStepUploadControls from './upload-controls'

const UploadStep = ({ stepIndex, printSlot }: ShopFormStepProps) => {
  const { shopState, shopFormSteps } = useContext(PrintShopFormContext)
  const { shopFormStrings } = useContext(PrintShopFormStringsContext)

  const prints = useMemo(() => {
    if (!printSlot) {
      return []
    }

    const prints = shopState.prints ?? []

    return prints.filter((print) => print.printSlotType === printSlot.type)
  }, [printSlot, shopState])

  // Use first uploaded print for preview image
  const previewImage: SanityImageFragment | undefined = prints[0]?.image

  const title = printSlot
    ? getUploadSectionTitle(shopFormStrings, printSlot.type)
    : ''
  const description = printSlot
    ? getUploadSectionDescription(shopFormStrings, printSlot.type)
    : []

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 md:gap-10">
      <UploadStepPrintPreview
        printSlot={printSlot}
        previewImage={previewImage}
      />

      <div className="flex flex-col md:border md:border-gray-light bg-white">
        <div className="grow px-5 sm:px-8 py-8 space-y-6">
          <div className="space-y-3">
            <h3 className="leading-tight font-semibold text-[22px]">{title}</h3>
            {!!description && !Array.isArray(description) && <>{description}</>}
            {!!description && Array.isArray(description) && (
              <ComplexPortableText
                content={description}
                className="rc rc-text-base"
              />
            )}
          </div>

          <UploadStepUploadControls
            stepIndex={stepIndex}
            printSlot={printSlot}
            prints={prints}
          />
        </div>

        <div
          className="grow-0 flex justify-end gap-3 sm:gap-4 py-3 px-5 sm:px-8"
          style={{
            boxShadow: '0px -4px 10px 0px rgba(0, 0, 0, 0.03)',
          }}
        >
          {stepIndex > 0 && <ShopFormBackButton />}
          {stepIndex < shopFormSteps.length - 1 && <ShopFormNextButton />}
        </div>
      </div>
    </div>
  )
}

export default UploadStep
